"use client";
import React, { useEffect, useState, useMemo } from "react";
import { CreditCard, Lock, X, AlertCircle, Eye, EyeOff, CheckCircle, Download } from "lucide-react";

/**
 * Props:
 * open: boolean
 * onClose: () => void
 * onSuccess: (opaqueData) => void
 * onError: (message) => void
 * planSummary: {
 *   name: string
 *   price: string
 *   description?: string[]
 * }
 */

const BrandIcon = ({ type }) => {
    if (!type) return null;

    const icons = {
        Visa: (
            <svg width="44" height="28" viewBox="0 0 44 28" fill="none">
                <rect width="44" height="28" rx="4" fill="#1A1F71" />
                <text x="22" y="18" fontFamily="'Inter', -apple-system, sans-serif" fontSize="14" fontWeight="900" fill="white" textAnchor="middle" style={{ fontStyle: 'italic', letterSpacing: '-0.5px' }}>VISA</text>
            </svg>
        ),
        Mastercard: (
            <svg width="44" height="28" viewBox="0 0 44 28" fill="none">
                <rect width="44" height="28" rx="4" fill="#222" />
                <circle cx="18" cy="14" r="9" fill="#EB001B" />
                <circle cx="28" cy="14" r="9" fill="#F79E1B" fillOpacity="0.85" />
                <path d="M23 14c0-2.5.8-4.8 2.2-6.5-1.4-1-3.2-1.5-5.2-1.5-5 0-9 4-9 9s4 9 9 9c2 0 3.8-.5 5.2-1.5C23.8 18.8 23 16.5 23 14z" fill="#EB001B" />
            </svg>
        ),
        Amex: (
            <svg width="44" height="28" viewBox="0 0 44 28" fill="none">
                <rect width="44" height="28" rx="4" fill="#016FD0" />
                <text x="22" y="17.5" fontFamily="'Inter', -apple-system, sans-serif" fontSize="11" fontWeight="800" fill="white" textAnchor="middle" style={{ letterSpacing: '0.5px' }}>AMEX</text>
            </svg>
        ),
        Discover: (
            <svg width="44" height="28" viewBox="0 0 44 28" fill="none">
                <rect width="44" height="28" rx="4" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1" />
                <text x="17" y="17.5" fontFamily="'Inter', -apple-system, sans-serif" fontSize="9" fontWeight="800" fill="#111827" textAnchor="middle">DISC</text>
                <circle cx="28" cy="14" r="6" fill="#fb923c" />
            </svg>
        ),
    };

    return icons[type] || null;
};

const AcceptJsCardModal = ({
    open,
    onClose,
    onSuccess,
    onError,
    planSummary,
    isProcessing = false,
    errorMessage = null,
    enableFreeTrial = false,
    processingMessage = null,
    successUrl = null,
    isSuccess = false,
    currentStatus = null,
    onSuccessClose = null,
    isCharged = false
}) => {
    const [loading, setLoading] = useState(false);
    const [internalError, setInternalError] = useState(null);
    const [focusedField, setFocusedField] = useState(null);
    const [showCvv, setShowCvv] = useState(false);

    const activeError = errorMessage || internalError;
    const isActiveLoading = loading || isProcessing;

    const [card, setCard] = useState({
        number: "",
        expiry: "",
        cvv: "",
    });

    useEffect(() => {
        if (!document.getElementById("inter-font")) {
            const font = document.createElement("link");
            font.id = "inter-font";
            font.rel = "stylesheet";
            font.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap";
            document.head.appendChild(font);
        }

        if (!open) return;
        if (document.getElementById("acceptjs-script")) return;

        const isProduction = process.env.REACT_APP_AUTH_NET_ENVIRONMENT === 'production';
        const scriptUrl = isProduction
            ? "https://js.authorize.net/v1/Accept.js"
            : "https://jstest.authorize.net/v1/Accept.js";

        const script = document.createElement("script");
        script.id = "acceptjs-script";
        script.src = scriptUrl;
        script.async = true;
        document.body.appendChild(script);
    }, [open]);

    useEffect(() => {
        if (open) {
            setInternalError(null);
        }
    }, [open]);

    useEffect(() => {
        if (errorMessage) {
            setLoading(false);
        }
    }, [errorMessage]);

    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [open]);

    const formatCardNumber = (value) =>
        value.replace(/\s/g, "").match(/.{1,4}/g)?.join(" ") || value;

    const formatExpiry = (value) => {
        let v = value.replace(/\D/g, "").slice(0, 4);
        if (v.length >= 3) {
            return `${v.slice(0, 2)} / ${v.slice(2)}`;
        }
        return v;
    };

    const cardType = useMemo(() => {
        const c = card.number.replace(/\s/g, "");
        if (c.startsWith("4")) return "Visa";
        if (/^5[1-5]/.test(c)) return "Mastercard";
        if (/^3[47]/.test(c)) return "Amex";
        if (/^6(?:011|5)/.test(c)) return "Discover";
        return null;
    }, [card.number]);

    const isFormValid = useMemo(() => {
        const cleaned = card.number.replace(/\s/g, "");
        const [m, y] = card.expiry.split(" / ");
        const expMonth = parseInt(m);
        const expYear = parseInt(`20${y}`);
        const now = new Date();
        const isExpired =
            !y || y.length < 2 ||
            expYear < now.getFullYear() ||
            (expYear === now.getFullYear() && expMonth < (now.getMonth() + 1));
        const isAmex = cleaned.startsWith("34") || cleaned.startsWith("37");

        return (
            cleaned.length >= 13 &&
            expMonth >= 1 &&
            expMonth <= 12 &&
            y?.length === 2 &&
            (isAmex ? card.cvv.length === 4 : card.cvv.length === 3) &&
            !isExpired
        );
    }, [card]);

    const getDisplayError = (msg) => {
        if (!msg) return null;
        const lowMsg = msg.toLowerCase();

        // Mask critical internal configuration failures
        if (
            lowMsg.includes("merchantcustomerid") ||
            lowMsg.includes("maxlength") ||
            lowMsg.includes("datatype") ||
            lowMsg.includes("transactionkey") ||
            lowMsg.includes("apiloginid") ||
            lowMsg.includes("schema")
        ) {
            return "Payment gateway encountered a configuration issue. Please contact support or try again later.";
        }

        return msg;
    };

    const handleSubmit = () => {
        setInternalError(null);

        if (!window.Accept) {
            setInternalError("Payment service is still loading, please wait.");
            return;
        }

        if (!isFormValid) {
            setInternalError("Please check your card details.");
            return;
        }

        setLoading(true);

        window.Accept.dispatchData(
            {
                authData: {
                    clientKey: process.env.REACT_APP_AUTHNET_CLIENT_KEY,
                    apiLoginID: process.env.REACT_APP_AUTHNET_API_LOGIN_ID,
                },
                cardData: {
                    cardNumber: card.number.replace(/\s/g, ""),
                    month: card.expiry.split(" / ")[0],
                    year: `20${card.expiry.split(" / ")[1]}`,
                    cardCode: card.cvv,
                },
            },
            (response) => {
                if (response.messages.resultCode === "Ok") {
                    onSuccess(response.opaqueData);
                } else {
                    setLoading(false);
                    const msg = response.messages.message?.[0]?.text || "Card verification failed.";
                    setInternalError(msg);
                    if (onError) onError(msg);
                }
            }
        );
    };

    if (!open) return null;

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <div style={styles.planBadge}>
                        <CreditCard size={20} color={colors.primary} />
                    </div>
                    <div style={styles.planInfo}>
                        <h2 style={styles.planName}>{planSummary.name} Plan</h2>
                        <p style={styles.planPrice}>
                            {planSummary.price === "$0.00" ? (isCharged ? "Previously Paid" : "Calculating...") : planSummary.price}
                            <span style={{ fontSize: "12px", opacity: 0.7 }}>billed monthly</span>
                            {enableFreeTrial && <span style={styles.trialBadge}>14-Day Free Trial</span>}
                        </p>
                    </div>
                    <button style={styles.closeBtn} onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {(successUrl || isSuccess) ? (
                    <div style={styles.successView}>
                        <div style={styles.successIconContainer}>
                            <CheckCircle size={48} color="#10b981" strokeWidth={2.5} />
                        </div>
                        <h2 style={styles.successTitle}>Setup Complete!</h2>
                        <p style={styles.successBody}>
                            Your subscription is now active! Your store is ready and your initial credits are available.
                        </p>

                        <div style={styles.successActions}>
                            {successUrl ? (
                                <a
                                    href={successUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={styles.downloadBtn}
                                >
                                    <Download size={18} />
                                    <span>Download Invoice</span>
                                </a>
                            ) : (
                                <div style={{
                                    ...styles.downloadBtn,
                                    opacity: 0.7,
                                    cursor: 'default',
                                    backgroundColor: '#f3f4f6',
                                    borderStyle: 'dashed'
                                }}>
                                    <Download size={18} color="#9ca3af" />
                                    <span style={{ color: '#6b7280' }}>Invoice is being generated...</span>
                                </div>
                            )}

                            <button
                                onClick={onSuccessClose}
                                style={styles.doneBtn}
                            >
                                Go to Dashboard
                            </button>
                        </div>
                    </div>
                ) : isCharged ? (
                    <div style={styles.recoveryView}>
                        <div style={styles.recoveryIconContainer}>
                            <AlertCircle size={48} color={colors.primary} />
                        </div>
                        <h2 style={styles.recoveryTitle}>Payment Received!</h2>
                        <p style={styles.recoveryBody}>
                            We've successfully received your payment{planSummary.price !== "$0.00" ? ` of ${planSummary.price}` : ""}.
                            However, we encountered a technical issue while finalizing your recurring subscription.
                        </p>
                        <p style={{ ...styles.recoveryBody, marginTop: '8px', fontSize: '13px', color: colors.textSecondary }}>
                            No further charges will be made. Click the button below to complete your setup.
                        </p>

                        <div style={styles.footer}>
                            <button
                                onClick={() => onSuccess(null)}
                                disabled={isActiveLoading || isSuccess}
                                style={{
                                    ...styles.payButton,
                                    ...(isActiveLoading || isSuccess ? styles.payButtonDisabled : {}),
                                }}
                            >
                                {isActiveLoading ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                        <div style={styles.loadingSpinnerSmall}></div>
                                        <span>Activating...</span>
                                    </div>
                                ) : "Complete Activation"}
                            </button>
                            {!isActiveLoading && (
                                <button onClick={onClose} style={styles.cancelBtn}>
                                    Cancel
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        {isActiveLoading && !activeError && (
                            <div style={styles.processingOverlay}>
                                <div style={styles.spinnerContainer}>
                                    <div style={styles.premiumSpinner}></div>
                                    <div style={styles.spinnerPulse}></div>
                                </div>
                                <h3 style={styles.processingHeading}>
                                    {isSuccess ? "Activation Complete!" : (isProcessing ? (processingMessage || "Finalizing Setup...") : "Verifying Card...")}
                                </h3>
                                <p style={styles.processingSubtext}>
                                    This usually takes a few seconds. Do not refresh.
                                </p>
                                <div style={styles.securityWarning}>
                                    <Lock size={18} strokeWidth={3} />
                                    <span>Please don’t refresh or close this window to avoid duplicate charges.</span>
                                </div>
                            </div>
                        )}

                        {enableFreeTrial && !isActiveLoading && !activeError && (
                            <div style={styles.trialBanner}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700' }}>
                                        <CheckCircle size={14} color="#10b981" />
                                        Includes 4,000 free credits.
                                    </div>
                                    <div style={{ fontSize: '11px', opacity: 0.9 }}>
                                        Your card will not be charged today. Billing begins after your 14-day trial.
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeError && (
                            <div style={styles.errorBanner}>
                                <AlertCircle size={14} />
                                <span style={{ flex: 1 }}>{getDisplayError(activeError)}</span>
                            </div>
                        )}

                        <div style={{
                            ...styles.formContainer,
                            opacity: isActiveLoading ? 0.3 : 1,
                            pointerEvents: isActiveLoading ? 'none' : 'auto',
                            filter: isActiveLoading ? 'blur(2px)' : 'none'
                        }}>
                            <div style={styles.inputGroup}>
                                <label style={styles.label}>Card details</label>
                                <div style={{ ...styles.inputWrapper, ...(focusedField === 'number' ? styles.inputWrapperFocused : {}) }}>
                                    <CreditCard size={18} color="#9ca3af" style={styles.inputIcon} />
                                    <input
                                        style={styles.input}
                                        value={card.number}
                                        onChange={(e) => setCard({ ...card, number: formatCardNumber(e.target.value) })}
                                        onFocus={() => setFocusedField('number')}
                                        onBlur={() => setFocusedField(null)}
                                        placeholder="1234 1234 1234 1234"
                                        maxLength={19}
                                        autoComplete="cc-number"
                                    />
                                    {cardType && (
                                        <div style={styles.cardTypeIcon}>
                                            <BrandIcon type={cardType} />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={styles.row}>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Expiry date</label>
                                    <div style={{ ...styles.inputWrapper, ...(focusedField === 'expiry' ? styles.inputWrapperFocused : {}) }}>
                                        <input
                                            style={{ ...styles.input, paddingLeft: '12px' }}
                                            placeholder="MM / YY"
                                            value={card.expiry}
                                            onChange={(e) => setCard({ ...card, expiry: formatExpiry(e.target.value) })}
                                            onFocus={() => setFocusedField('expiry')}
                                            onBlur={() => setFocusedField(null)}
                                            maxLength={7}
                                        />
                                    </div>
                                </div>

                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>CVC</label>
                                    <div style={{ ...styles.inputWrapper, ...(focusedField === 'cvv' ? styles.inputWrapperFocused : {}) }}>
                                        <Lock size={16} color="#9ca3af" style={styles.inputIcon} />
                                        <input
                                            type={showCvv ? "text" : "password"}
                                            style={styles.input}
                                            placeholder="123"
                                            value={card.cvv}
                                            onChange={(e) => setCard({ ...card, cvv: e.target.value })}
                                            onFocus={() => setFocusedField('cvv')}
                                            onBlur={() => setFocusedField(null)}
                                            maxLength={4}
                                        />
                                        <button
                                            onClick={() => setShowCvv(!showCvv)}
                                            style={styles.eyeBtn}
                                            type="button"
                                        >
                                            {showCvv ? <EyeOff size={18} color="#6b7280" /> : <Eye size={18} color="#6b7280" />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div style={styles.securityNote}>
                                <Lock size={12} color="#10b981" />
                                <span>Secured with 256-bit SSL encryption</span>
                            </div>
                        </div>

                        <div style={styles.footer}>
                            <div style={styles.totalRow}>
                                <span style={styles.totalLabel}>Total due today</span>
                                <span style={styles.totalAmount}>
                                    {isCharged ? (planSummary.price !== "$0.00" ? planSummary.price : "PAID") : (enableFreeTrial ? "$0.00" : planSummary?.price)}
                                </span>
                            </div>

                            <button
                                onClick={handleSubmit}
                                disabled={isActiveLoading || !isFormValid || isSuccess}
                                style={{
                                    ...styles.payButton,
                                    ...(isActiveLoading || !isFormValid || isSuccess ? styles.payButtonDisabled : {}),
                                }}
                            >
                                {isActiveLoading ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                        <div style={styles.loadingSpinnerSmall}></div>
                                        <span>{isCharged ? "Finalizing subscription..." : "Securing payment..."}</span>
                                    </div>
                                ) : isSuccess ? (
                                    <span>Success!</span>
                                ) : (
                                    <>{isCharged ? "Complete Activation" : (enableFreeTrial ? "Start 14-day free trial" : `Confirm & pay ${planSummary?.price}`)}</>
                                )}
                            </button>

                            {!isActiveLoading && (
                                <button onClick={onClose} style={styles.cancelBtn}>
                                    Cancel
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const colors = {
    primary: '#2563eb',
    primaryDark: '#1d4ed8',
    bg: '#ffffff',
    text: '#111827',
    textSecondary: '#6b7280',
    border: '#e5e7eb',
    errorBg: '#fef2f2',
    errorText: '#b91c1c',
    focusRing: 'rgba(37, 99, 235, 0.2)',
};

const styles = {
    overlay: {
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(17, 24, 39, 0.7)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        animation: "fadeIn 0.2s ease-out",
    },
    modal: {
        background: colors.bg,
        borderRadius: "24px",
        width: "100%",
        maxWidth: "480px",
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        overflow: "hidden",
        position: "relative",
        margin: "16px",
        animation: "slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    header: {
        padding: "24px 32px",
        borderBottom: `1px solid ${colors.border}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: "#ffffff",
    },
    planBadge: {
        width: "40px",
        height: "40px",
        borderRadius: "10px",
        background: "#eff6ff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid #dbeafe",
        marginRight: "14px",
    },
    planInfo: {
        flexGrow: 1,
    },
    planName: {
        margin: 0,
        fontSize: "18px",
        fontWeight: "700",
        color: colors.text,
        letterSpacing: "-0.01em",
    },
    planPrice: {
        margin: "4px 0 0",
        fontSize: "14px",
        color: colors.primary,
        fontWeight: "700",
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    trialBadge: {
        fontSize: "11px",
        background: "#f0fdf4",
        color: "#166534",
        padding: "2px 8px",
        borderRadius: "100px",
        border: "1px solid #dcfce7",
        fontWeight: "600",
    },
    closeBtn: {
        background: "transparent",
        border: "none",
        cursor: "pointer",
        padding: "8px",
        borderRadius: "50%",
        transition: "background 0.2s",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#9ca3af",
    },
    trialBanner: {
        margin: "12px 24px 0",
        padding: "10px 14px",
        background: "#f0fdf4",
        border: "1px solid #dcfce7",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        color: "#166534",
        fontSize: "12px",
        animation: "slideUp 0.3s ease-out",
        lineHeight: "1.4",
    },
    formContainer: {
        padding: "20px 24px 24px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        transition: "all 0.3s ease",
    },
    row: {
        display: "grid",
        gridTemplateColumns: "1.2fr 1fr",
        gap: "16px",
    },
    inputGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
    },
    label: {
        fontSize: "12px",
        fontWeight: "600",
        color: colors.text,
        marginBottom: "8px",
        display: "block",
        letterSpacing: "0.2px",
    },
    inputWrapper: {
        position: "relative",
        borderRadius: "10px",
        border: `1px solid ${colors.border}`,
        background: "#fff",
        transition: "all 0.2s ease",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        height: "44px",
    },
    inputWrapperFocused: {
        borderColor: colors.primary,
        boxShadow: `0 0 0 3px ${colors.focusRing}`,
    },
    inputIcon: {
        marginLeft: "12px",
        marginRight: "4px",
    },
    input: {
        width: "100%",
        height: "100%",
        border: "none",
        outline: "none",
        fontSize: "15px",
        color: colors.text,
        padding: "0 12px 0 4px",
        background: "transparent",
        fontWeight: "500",
        fontFamily: "inherit",
    },
    cardTypeIcon: {
        position: "absolute",
        right: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        pointerEvents: "none",
        animation: "fadeIn 0.2s ease-out",
        height: "28px",
        width: "44px",
    },
    eyeBtn: {
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: "0 12px",
        display: "flex",
        alignItems: "center",
    },
    errorBanner: {
        margin: "12px 24px 0",
        padding: "8px 12px",
        background: "#fff1f2",
        borderRadius: "6px",
        borderLeft: `3px solid ${colors.errorText}`,
        display: "flex",
        gap: "10px",
        alignItems: "center",
        color: "#991b1b",
        fontSize: "12px",
        lineHeight: "1.4",
        fontWeight: "500",
        animation: "slideUp 0.3s ease-out",
    },
    securityNote: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        fontSize: "12px",
        color: colors.textSecondary,
        marginTop: "12px",
        fontWeight: "500",
        letterSpacing: "0.01em",
    },
    footer: {
        padding: "20px 24px",
        background: "#ffffff",
        borderTop: `1px solid ${colors.border}`,
    },
    totalRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "20px",
        padding: "0 4px",
    },
    totalLabel: {
        fontSize: "14px",
        color: "#6b7280",
        fontWeight: "500",
    },
    totalAmount: {
        fontSize: "22px",
        fontWeight: "800",
        color: "#111827",
        letterSpacing: "-0.04em",
    },
    payButton: {
        width: "100%",
        background: colors.primary,
        color: "#fff",
        border: "none",
        borderRadius: "10px",
        padding: "12px",
        fontSize: "14px",
        fontWeight: "600",
        cursor: "pointer",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: "0 4px 6px -1px rgba(37, 99, 235, 0.1)",
        position: "relative",
    },
    payButtonDisabled: {
        opacity: 0.6,
        cursor: "not-allowed",
        background: "#cbd5e1",
        boxShadow: "none",
    },
    loadingSpinnerSmall: {
        width: "16px",
        height: "16px",
        border: "2px solid rgba(255,255,255,0.3)",
        borderRadius: "50%",
        borderTopColor: "#fff",
        animation: "spin 0.8s linear infinite",
    },
    processingOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(255, 255, 255, 0.65)",
        backdropFilter: "blur(16px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        borderRadius: "24px",
        padding: "32px",
        textAlign: "center",
    },
    spinnerContainer: {
        position: "relative",
        width: "60px",
        height: "60px",
        marginBottom: "20px",
    },
    premiumSpinner: {
        width: "60px",
        height: "60px",
        border: "3px solid #f3f4f6",
        borderRadius: "50%",
        borderTopColor: colors.primary,
        animation: "spin 1s cubic-bezier(0.5, 0.1, 0.5, 0.9) infinite",
    },
    spinnerPulse: {
        position: "absolute",
        top: "5px",
        left: "5px",
        width: "50px",
        height: "50px",
        border: `2px solid ${colors.primary}`,
        borderRadius: "50%",
        opacity: 0.1,
        animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
    },
    processingHeading: {
        fontSize: "18px",
        fontWeight: "700",
        color: colors.text,
        marginBottom: "8px",
    },
    processingSubtext: {
        fontSize: "14px",
        color: colors.textSecondary,
        marginBottom: "20px",
    },
    securityWarning: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        marginTop: "24px",
        color: colors.textSecondary,
        fontSize: "11px",
        fontWeight: "500",
        letterSpacing: "0.2px",
        opacity: 0.8,
        padding: "0 20px",
        lineHeight: "1.4",
        borderTop: "1px solid rgba(0,0,0,0.05)",
        paddingTop: "16px",
        width: "100%",
        maxWidth: "280px",
    },
    cancelBtn: {
        width: "100%",
        background: "transparent",
        border: "none",
        color: colors.textSecondary,
        fontSize: "12px",
        fontWeight: "500",
        marginTop: "12px",
        cursor: "pointer",
    },
    successView: {
        padding: "40px 32px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        animation: "fadeIn 0.4s ease-out",
    },
    successIconContainer: {
        width: "80px",
        height: "80px",
        background: "#f0fdf4",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "24px",
    },
    successTitle: {
        fontSize: "22px",
        fontWeight: "800",
        color: colors.text,
        marginBottom: "12px",
    },
    successBody: {
        fontSize: "15px",
        color: colors.textSecondary,
        lineHeight: "1.6",
        marginBottom: "32px",
        maxWidth: "320px",
    },
    successActions: {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        width: "100%",
    },
    recoveryView: {
        padding: "40px 32px",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        animation: "slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
    },
    recoveryIconContainer: {
        width: "80px",
        height: "80px",
        borderRadius: "50%",
        background: "#eff6ff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "24px",
    },
    recoveryTitle: {
        fontSize: "24px",
        fontWeight: "800",
        color: colors.text,
        margin: "0 0 12px",
        letterSpacing: "-0.02em",
    },
    recoveryBody: {
        fontSize: "15px",
        color: colors.text,
        lineHeight: "1.6",
        margin: "0",
        maxWidth: "320px",
    },
    downloadBtn: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        width: "100%",
        padding: "14px",
        background: "#f9fafb",
        border: `1px solid ${colors.border}`,
        borderRadius: "10px",
        color: colors.text,
        fontSize: "15px",
        fontWeight: "600",
        textDecoration: "none",
        transition: "all 0.2s",
    },
    doneBtn: {
        width: "100%",
        background: "transparent",
        color: colors.textSecondary,
        border: `1px solid ${colors.border}`,
        borderRadius: "12px",
        padding: "16px",
        fontSize: "15px",
        fontWeight: "600",
        cursor: "pointer",
        transition: "all 0.2s",
    }
};

if (typeof document !== 'undefined' && !document.getElementById('modal-animations')) {
    const styleSheet = document.createElement("style");
    styleSheet.id = 'modal-animations';
    styleSheet.innerText = `
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.1; }
            50% { transform: scale(1.1); opacity: 0.2; }
        }
        input [type="password"]::-ms-reveal,
        input [type="password"]::-ms-clear {
            display: none;
        }
    `;
    document.head.appendChild(styleSheet);
}

export default AcceptJsCardModal;
